/**
 * Общий набор иконок ASPID (AF-19).
 *
 * Здесь имена, объявленные **минимум в двух** приложениях на момент сведения:
 * общность доказана практикой, а не вкусом. Иконки, встречавшиеся у одного
 * приложения, остались у него и подключаются расширением — см. README.
 *
 * Два имени расходились по содержимому, оба разобраны:
 *
 * - `calendar` — deals и applications держали `faCalendarClock`, analyst
 *   `faCalendar`. Реальный потребитель один (deals, два места), у остальных
 *   объявление было мёртвым. Взят `faCalendarClock`: видимого изменения нет ни
 *   у кого.
 * - `projects` — deals `faUnicorn`, applications `faFolders`. В венчурном
 *   фонде «единорог» — доменный термин, а не заглушка, поэтому deals оставляет
 *   его оверрайдом. В общий набор взят `faFolders`: имя должно описывать
 *   содержимое, иначе приложение, взявшее `projects` из пакета, получит
 *   сюрприз.
 *
 * Файл собран из трёх исходных коллекций; порядок — по пакету FA, затем по
 * имени, чтобы диффы читались.
 */

import {
  faLightEmergencyOn as alert,
  faArrowLeft as arrowLeft,
  faArrowRight as arrowRight,
  faArrowRotateLeft as arrowRotateLeft,
  faArrowUpRightFromSquare as arrowUpRightFromSquare,
  faArrowUp as asc,
  faPaperclip as attachment,
  faBan as ban,
  faBell as bell,
  faBook as book,
  faBriefcase as briefcase,
  faBug as bug,
  faCalendarClock as calendar,
  faCheck as check,
  faChevronCircleLeft as chevronLeft,
  faChevronCircleRight as chevronRight,
  faCircleExclamation as circleExclamation,
  faTriangleExclamation as triangleExclamation,
  faClipboardCheck as clickBoard,
  faClock as clock,
  faRectangleXmark as close,
  faCrystalBall as crystalBall,
  faGaugeHigh as dashboard,
  faHandshake as deals,
  faArrowAltDown as desc,
  faCircleCheck as done,
  faCircleSmall as dot,
  faArrowDownToLine as download,
  faEarthAsia as earthAsia,
  faFilter as filter,
  faFunction as functionIcon,
  faSack as funds,
  faFilterCircleDollar as funnel,
  faGlobe as globe,
  faLightbulbExclamationOn as insight,
  faSquareKanban as kanban,
  faList as list,
  faLock as lock,
  faLockOpen as lockOpen,
  faArrowRightFromBracket as logOut,
  faFaceSwear as lostDeal,
  faBars as menu,
  faMessageExclamation as messageExclamation,
  faCirclePlus as newItem,
  faArrowUpRightFromSquare as openLink,
  faPenField as penField,
  faPenToSquare as penToSquare,
  faPeopleGroup as peopleGroup,
  faBolt as performance,
  faArrowProgress as pipeline,
  faPlus as plus,
  faBriefcaseBlank as portfolios,
  faFolders as projects,
  faQuestionCircle as questionCircle,
  faMagnifyingGlass as search,
  faPipe as separator,
  faGear as settings,
  faSignOutAlt as signOut,
  faArrowUpArrowDown as sort,
  faChartColumn as statistics,
  faTrashCan as trashCan,
  faTrashCanList as trashList,
  faUpload as upload,
  faCloudArrowUp as uploadCloud,
  faUsers as users,
  faEllipsisVertical as verticalDots,
  faCalendarClock as waitList,
  faWallet as wallet,
  faWifiSlash as wifiDisappeared,
  faHandsClapping as win,
  faPersonDigging as workInProgress,
  faXmark as xmark,
} from '@fortawesome/pro-light-svg-icons';
import {
  /**
   * Второй вес того же кружка, а не вторая иконка (AF-579).
   *
   * `circleExclamation` из `pro-light` остаётся у четырёх прежних мест: там
   * знак стоит рядом с текстом, и тонкая обводка ему к лицу. В чипе канбана
   * он сидит один на месте аватара при 14 px — линия light тоньше волоса и
   * рядом с насыщенными пастельными аватарами читается слабее того, что
   * заменяет. Выбор веса сделан владельцем 27.09.2026 по показу пяти
   * вариантов рядом с эталонным аватаром.
   *
   * Менять вес у общего `circleExclamation` было НЕЛЬЗЯ: он молча перекрасил
   * бы три чужих экрана ради одного чипа.
   */
  faCircleExclamation as circleExclamationRegular,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faXmark as xmarkSolid,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faPaperclip as attachmentThin,
  faFileAudio as audioThin,
  faBell as bellThin,
  faCaretDown as caretDownThin,
  faCaretUp as caretUpThin,
  faFileCsv as csvThin,
  faDownload as downloadFileThin,
  faFileExcel as excelThin,
  faFile as fileThin,
  faFileImage as imageThin,
  faFilePdf as pdfThin,
  faFilePpt as pptThin,
  faQuestionCircle as questionCircleThin,
  faPipe as separatorThin,
  faFileLines as textThin,
  faTrashCan as trashCanThin,
  faFileVideo as videoThin,
  faFileWord as wordThin,
} from '@fortawesome/pro-thin-svg-icons';

export const IconCollection = {
  alert,
  arrowLeft,
  arrowRight,
  arrowRotateLeft,
  arrowUpRightFromSquare,
  asc,
  attachment,
  attachmentThin,
  audioThin,
  ban,
  bell,
  bellThin,
  book,
  briefcase,
  bug,
  calendar,
  caretDownThin,
  caretUpThin,
  check,
  chevronLeft,
  chevronRight,
  circleExclamation,
  circleExclamationRegular,
  triangleExclamation,
  clickBoard,
  clock,
  close,
  crystalBall,
  csvThin,
  dashboard,
  deals,
  desc,
  done,
  dot,
  download,
  downloadFileThin,
  earthAsia,
  excelThin,
  fileThin,
  filter,
  functionIcon,
  funds,
  funnel,
  globe,
  imageThin,
  insight,
  kanban,
  list,
  lock,
  lockOpen,
  logOut,
  lostDeal,
  menu,
  messageExclamation,
  newItem,
  openLink,
  pdfThin,
  penField,
  penToSquare,
  peopleGroup,
  performance,
  pipeline,
  plus,
  portfolios,
  pptThin,
  projects,
  questionCircle,
  questionCircleThin,
  search,
  separator,
  separatorThin,
  settings,
  signOut,
  sort,
  statistics,
  textThin,
  trashCan,
  trashCanThin,
  trashList,
  upload,
  uploadCloud,
  users,
  verticalDots,
  videoThin,
  waitList,
  wallet,
  wifiDisappeared,
  win,
  wordThin,
  workInProgress,
  xmark,
  xmarkSolid,
};
